import { BASE_URL } from 'core/constants/general.constants';
import { IBusiness } from 'core/model/interfaces/business.interface';
import { IUser } from 'core/model/interfaces/customer.interface';
import {
  IInsiderCart,
  IInsiderCartItem,
  IInsiderProduct,
  IInsiderTransaction,
  IInsiderUser,
} from 'core/model/interfaces/insider.interface';
import { IOrderCreated, IOrderDetailItem } from 'core/model/interfaces/order.interface';
import { IPaymentType } from 'core/model/interfaces/payment-type.interface';
import { IBaseProduct, ICartProduct, IProduct, IProductDetail } from 'core/model/interfaces/product.interface';
import { cartProductToProduct, getSelectedUnitMeasure } from './products.utils';

export const cutomerToInsiderUser = (customer: IUser, addresses: Array<IBusiness>): IInsiderUser => {
  const { basicData: user, priceGroup, id, priceList, dex, apudex } = customer;
  const primaryPhone = user.phones?.find(u => u.isMain)?.number;
  const totalPaymentMethods: Array<IPaymentType> = [...(dex?.paymentMethods || []), ...(apudex?.paymentMethods || [])];

  return {
    email: user.email ?? '',
    uuid: user.document?.number,
    name: user.name,
    phone_number: `+51${primaryPhone}`,
    surname: user.firstLastName,
    gdpr_optin: true,
    email_optin: true,
    sms_optin: true,
    whatsapp_optin: true,
    custom: {
      customerid: id,
      workArea: priceGroup.id,
      subWorkArea: priceList.id,
      segment: dex?.segment ?? '',
      typeDocument: user.document?.type,
      numberDocument: user.document?.number,
      secondLastName: user.secondLastName,
      businessid: addresses?.map(bi => bi.id)?.join(),
      address: addresses?.map(bi => bi.address)?.join(),
      district: addresses?.map(bi => bi.district)?.join(),
      province: addresses?.map(bi => bi.province)?.join(),
      department: addresses?.map(bi => bi.department)?.join(),
      ubigeoCode: addresses?.map(bi => bi.ubigeoCode)?.join(),
      sellinsid: totalPaymentMethods?.map(si => si.id)?.join(),
      sellinspaymentType: totalPaymentMethods?.map(si => si.description)?.join(),
      documenttype: user.document?.type,
      documentnumber: user.document?.number,
    },
  };
};

export const productListToInsiderProductList = (products: Array<IProduct>): Array<IInsiderProduct> =>
  products.map(productToInsiderProduct);

export const productToInsiderProduct = (product: IProduct): IInsiderProduct => {
  const {
    originalPrice,
    price,
    presentation,
    code: selectedUnitMeasureCode,
    promoIndicators,
  } = getSelectedUnitMeasure(product.unitMeasures);

  const baseUrl = BASE_URL || '';
  const taxonomy = [product.categoryName, product.subcategoryName].filter(Boolean);

  return {
    id: product.sku,
    name: product.name,
    taxonomy,
    unit_price: originalPrice,
    unit_sale_price: price,
    url: `${baseUrl}producto/${product.sku}`,
    stock: product.stock,
    product_image_url: product.image,

    custom: {
      presentation,
      hasDiscount: promoIndicators.hasAnyDiscount,
      providerDisplayName: product.providerDisplayName,
      sourceId: product.sourceId,
      subcategoryName: product.subcategoryName,
      categoryName: product.categoryName,
      taxRate: product.taxRate,
      selectedUnitMeasureCode,
      brand: product.brand,
      sku: product.sku,
      gtin: product.eanCode,
    },
  };
};

/**
 * Mapper for IBaseProduct - used in cart add/remove events
 * IBaseProduct doesn't have stock field
 */
export const baseProductToInsiderProduct = (product: IBaseProduct): IInsiderProduct => {
  const {
    originalPrice,
    price,
    presentation,
    code: selectedUnitMeasureCode,
    promoIndicators,
  } = getSelectedUnitMeasure(product.unitMeasures);

  const baseUrl = BASE_URL || '';
  const taxonomy = [product.categoryName, product.subcategoryName].filter(Boolean);

  return {
    id: product.sku,
    name: product.name,
    taxonomy,
    unit_price: originalPrice,
    unit_sale_price: price,
    url: `${baseUrl}producto/${product.sku}`,
    product_image_url: product.image,

    custom: {
      presentation,
      hasDiscount: promoIndicators.hasAnyDiscount,
      providerDisplayName: product.providerDisplayName,
      sourceId: product.sourceId,
      subcategoryName: product.subcategoryName,
      categoryName: product.categoryName,
      taxRate: product.taxRate,
      selectedUnitMeasureCode,
      brand: product.brand,
      sku: product.sku,
      gtin: product.eanCode,
      ...(product.distributorId && { distributorCode: product.distributorId }),
    },
  };
};

export const productDetailToInsiderProduct = (product: IProductDetail): IInsiderProduct => {
  const {
    originalPrice,
    price,
    presentation,
    code: selectedUnitMeasureCode,
    promoIndicators: { hasAnyDiscount },
    stock,
  } = getSelectedUnitMeasure(product.unitMeasures);

  const baseUrl = BASE_URL || '';
  const taxonomy = [product.categoryName, product.subcategoryName].filter(Boolean);

  return {
    id: product.sku,
    name: product.name,
    taxonomy,
    unit_price: originalPrice,
    unit_sale_price: price,
    url: `${baseUrl}producto/${product.sku}`,
    stock,
    product_image_url: product.image,

    custom: {
      presentation,
      hasDiscount: hasAnyDiscount,
      providerDisplayName: product.providerDisplayName,
      sourceId: product.sourceId,
      subcategoryName: product.subcategoryName,
      categoryName: product.categoryName,
      taxRate: product.taxRate,
      selectedUnitMeasureCode,
      unitMeasuresCode: product.unitMeasures.map(uM => uM.code)?.join(),
      brand: product.brand,
      description: product.description,
      sku: product.sku,
      gtin: product.eanCode,
      distributorCode: product.distributor?.id,
    },
  };
};

export const cartToInsiderCart = (products: Array<ICartProduct>, total: number, shippingCost = 0): IInsiderCart => ({
  total,
  shipping_cost: shippingCost,
  items: products.map(cartProduct => {
    const productData = productToInsiderProduct(cartProductToProduct(cartProduct));

    return {
      ...productData,
      quantity: cartProduct.quantity,
    };
  }),
});

const mapProductsToLineItemsInsider = (product: IOrderDetailItem, orderId: number): IInsiderCartItem => {
  const baseUrl = BASE_URL || '';
  const taxonomy = [product.categoryName, product.subcategoryName].filter(Boolean);

  return {
    id: product.sku,
    name: product.name,
    taxonomy,
    unit_price: product.price,
    unit_sale_price: product.price,
    quantity: product.quantity,
    url: `${baseUrl}producto/${product.sku}`,
    product_image_url: product.image,

    custom: {
      orderId,
      presentation: product.presentation,
      hasDiscount: product.hasDiscount,
      providerDisplayName: product.displayName,
      distributorName: product.distributorName,
      categoryName: product.categoryName,
      subcategoryName: product.subcategoryName,
      brand: product.brand,
      sku: product.sku,
      gtin: product.eanCode,
    },
  };
};

export const orderDetailToInsiderPurchase = (orders: Array<IOrderCreated>): IInsiderTransaction => {
  const order_id = orders[0].orderId?.toString();
  const total = orders.reduce((n, { totalAmount }) => n + totalAmount, 0);
  const shipping_cost = 0;
  const items: Array<IInsiderCartItem> = [];

  orders.forEach(order => {
    const orderItems = order.products.map(product => mapProductsToLineItemsInsider(product, order.orderId));
    items.push(...orderItems);
  });

  return {
    order_id,
    total,
    shipping_cost,
    items,
  };
};
